/**
 * MergeDialog — the one merge dialog (the "Review duplicates" page and Operator detail both open
 * it). Corbin, 2026-10-20: he nearly merged the OK DVN row, the one with the hauler, INTO the TX
 * row, because the old dialog made "which one survives" easy to flip. So:
 *
 *   - KEEP (green) and MERGE AWAY (grey) side by side, the suggested survivor pre-selected as
 *     KEEP with its reason; when both rows are in use none is pre-picked and he chooses;
 *   - a Swap button, which re-runs the preview;
 *   - the preview's exact counts (the real merge run in a transaction and rolled back);
 *   - red warnings when MERGE AWAY has something KEEP lacks, blocking Confirm until
 *     "I understand" is ticked;
 *   - what happens to each field (abbreviation kept or lost, display name, state);
 *   - the final display name, editable, saved on KEEP with the merge;
 *   - a Confirm button that names both sides.
 */
import * as React from 'react';
import { toast } from 'sonner';
import { ApiError } from '@lodex-pro/web-shared';
import { ArrowLeftRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { fieldStyle, subText, Pill } from '../../ui/bits';
import { mergeError } from '../abbreviations/hooks';
import { useMergeInto, useMergePreview, type MergePreview, type MergeSide } from './dedupe-hooks';
import { formatNumber } from '@lodex-pro/shared/number-format';

/** One side of the merge as the opener knows it, before the preview loads. */
export interface MergeCandidate {
  readonly id: string;
  readonly name: string;
  readonly state: string | null;
}

export interface MergeDialogProps {
  /** The two rows, or null when closed. */
  readonly pair: readonly [MergeCandidate, MergeCandidate] | null;
  /** The row to pre-select as KEEP; null when he must choose (both in use). */
  readonly suggestedKeepId: string | null;
  readonly onOpenChange: (open: boolean) => void;
  readonly onMerged?: (keepId: string) => void;
}

const sideLabel = (s: { state: string | null; name: string }): string =>
  `${s.state ?? '—'} ${s.name}`;

/** The API's refusals, in words. */
export function mergeDialogError(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code === 'OPERATOR_MERGE_BUSY') {
      return 'Those operators are busy right now (live work holds them). Try again in a moment.';
    }
    if (err.code === 'OPERATOR_MERGE_WARNINGS_CHANGED') {
      return 'Something about these operators changed since the preview. Review the new one.';
    }
    const reason = err.fields.find((f) => f.path === 'sourceOperatorId')?.reason;
    if (reason === 'would_create_merge_cycle') {
      return 'The kept row is already merged into the other one. Swap the direction instead.';
    }
    if (reason === 'target_chain_already_cyclic') {
      return 'The kept row is part of a broken merge chain. It needs repairing before any merge.';
    }
    if (reason === 'cannot_merge_into_self') return 'A row cannot be merged into itself.';
  }
  return mergeError(err);
}

export function MergeDialog({
  pair,
  suggestedKeepId,
  onOpenChange,
  onMerged,
}: MergeDialogProps): React.JSX.Element {
  const open = pair !== null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent style={{ maxWidth: '880px', width: 'calc(100vw - 32px)' }}>
        {pair !== null && (
          // Keyed by the pair: a new pair starts fresh (choice, tick, typed name).
          <MergeBody
            key={`${pair[0].id}:${pair[1].id}`}
            pair={pair}
            suggestedKeepId={suggestedKeepId}
            onClose={(): void => onOpenChange(false)}
            onMerged={onMerged}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function MergeBody({
  pair,
  suggestedKeepId,
  onClose,
  onMerged,
}: {
  pair: readonly [MergeCandidate, MergeCandidate];
  suggestedKeepId: string | null;
  onClose: () => void;
  onMerged?: ((keepId: string) => void) | undefined;
}): React.JSX.Element {
  const [keepId, setKeepId] = React.useState<string | null>(
    suggestedKeepId !== null && pair.some((p) => p.id === suggestedKeepId) ? suggestedKeepId : null,
  );
  const keep = pair.find((p) => p.id === keepId) ?? null;
  const away = keep === null ? null : (pair.find((p) => p.id !== keep.id) ?? null);

  const preview = useMergePreview(keep?.id ?? pair[0].id);
  const merge = useMergeInto(keep?.id ?? pair[0].id);
  const [understood, setUnderstood] = React.useState(false);
  const [finalName, setFinalName] = React.useState('');
  // The preview for the CURRENT direction only: a late answer for a direction he has since
  // swapped away from is dropped (`asked` counts the requests).
  const [p, setP] = React.useState<MergePreview | null>(null);
  const [previewError, setPreviewError] = React.useState<unknown>(null);
  const asked = React.useRef(0);
  // Bumped to re-run the preview for the same direction (the server said it changed).
  const [round, setRound] = React.useState(0);

  // Every direction gets its own preview, tick and default display name.
  const runPreview = preview.mutate;
  React.useEffect(() => {
    setUnderstood(false);
    setP(null);
    setPreviewError(null);
    if (keep === null || away === null) return;
    const n = ++asked.current;
    runPreview(
      { sourceOperatorId: away.id },
      {
        onSuccess: (r) => {
          if (n !== asked.current) return;
          setP(r);
          setFinalName(r.fields.displayName.proposed);
        },
        onError: (err) => {
          if (n === asked.current) setPreviewError(err);
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-run per direction, or when asked again
  }, [keep?.id, away?.id, round]);

  const ready = p !== null && keep !== null && away !== null;
  const blocked = ready && p.warnings.length > 0 && !understood;

  const swap = (): void => {
    if (away !== null) setKeepId(away.id);
  };

  const confirm = (): void => {
    if (!ready) return;
    const name = finalName.trim();
    merge.mutate(
      {
        sourceOperatorId: away.id,
        // Only when he changed it: otherwise the merge's own rule leaves the same name.
        ...(name !== '' && name !== p.fields.displayName.proposed ? { displayName: name } : {}),
        // Confirm is enabled only once these are ticked; the server re-checks them.
        acknowledgedWarnings: p.warnings.map((w) => w.code),
      },
      {
        onSuccess: (r) => {
          toast.success(
            `Merged: moved ${String(r.wellsMoved)} wells and ${String(
              r.haulersMoved + r.haulersDeleted,
            )} hauler customer records into ${r.displayName ?? keep.name}`,
          );
          onMerged?.(keep.id);
          onClose();
        },
        onError: (err) => {
          toast.error(mergeDialogError(err), { duration: 12_000 });
          // The warnings changed under him: show the current preview, unticked.
          if (err instanceof ApiError && err.code === 'OPERATOR_MERGE_WARNINGS_CHANGED') {
            setRound((r) => r + 1);
          }
        },
      },
    );
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>Merge operators</DialogTitle>
      </DialogHeader>

      {keep === null ? (
        <ChooseKeep pair={pair} onChoose={setKeepId} />
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) auto minmax(0, 1fr)',
              gap: '12px',
              alignItems: 'stretch',
            }}
          >
            <SideCard role="KEEP" fallback={keep} side={p?.keep} />
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Button
                type="button"
                variant="outline"
                onClick={swap}
                disabled={merge.isPending}
                aria-label="Swap direction"
                title="Swap direction"
              >
                <ArrowLeftRight size={16} aria-hidden="true" /> Swap
              </Button>
            </div>
            <SideCard role="MERGE AWAY" fallback={away ?? keep} side={p?.mergeAway} />
          </div>

          {p === null && previewError === null && (
            <p style={{ ...subText, marginTop: '12px' }}>Working out the effect…</p>
          )}
          {previewError !== null && (
            <p
              role="alert"
              style={{ color: 'var(--color-danger)', marginTop: '12px', fontSize: '14px' }}
            >
              {mergeDialogError(previewError)}
            </p>
          )}

          {ready && (
            <PreviewBody
              p={p}
              finalName={finalName}
              onFinalName={setFinalName}
              understood={understood}
              onUnderstood={setUnderstood}
            />
          )}
        </>
      )}

      <DialogFooter style={{ marginTop: '12px' }}>
        <Button type="button" variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={!ready || blocked || merge.isPending}
          onClick={confirm}
          style={{
            backgroundColor: 'var(--color-danger)',
            color: 'var(--color-on-accent)',
            whiteSpace: 'normal',
          }}
        >
          {merge.isPending
            ? 'Merging…'
            : ready
              ? `Merge ${sideLabel(p.mergeAway)} → into ${sideLabel(p.keep)}${
                  p.keep.abbreviation !== null ? ` (${p.keep.abbreviation})` : ''
                }`
              : 'Merge'}
        </Button>
      </DialogFooter>
    </>
  );
}

function ChooseKeep({
  pair,
  onChoose,
}: {
  pair: readonly [MergeCandidate, MergeCandidate];
  onChoose: (id: string) => void;
}): React.JSX.Element {
  return (
    <div>
      <p role="alert" style={{ fontSize: '14px', color: 'var(--color-warning)' }}>
        Both rows are in use (haulers and an abbreviation each). Choose which one to keep.
      </p>
      <div
        style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '10px' }}
      >
        {pair.map((c) => (
          <Button key={c.id} type="button" variant="outline" onClick={(): void => onChoose(c.id)}>
            Keep {sideLabel(c)}
          </Button>
        ))}
      </div>
    </div>
  );
}

function SideCard({
  role,
  fallback,
  side,
}: {
  role: 'KEEP' | 'MERGE AWAY';
  fallback: MergeCandidate;
  side: MergeSide | undefined;
}): React.JSX.Element {
  const keep = role === 'KEEP';
  return (
    <section
      aria-label={role}
      style={{
        border: `2px solid ${keep ? 'var(--color-success)' : 'var(--color-border-primary)'}`,
        background: keep ? 'rgba(111, 207, 142,0.06)' : 'var(--color-bg-tertiary)',
        borderRadius: '8px',
        padding: '12px',
        minWidth: 0,
      }}
    >
      <Pill label={role} tone={keep ? 'positive' : 'muted'} />
      <p style={{ fontWeight: 600, margin: '8px 0 2px', overflowWrap: 'anywhere' }}>
        {sideLabel(side ?? fallback)}
      </p>
      {side !== undefined && (
        <>
          {side.displayName !== null && (
            <p style={{ ...subText, textTransform: 'none' }}>Shown as {side.displayName}</p>
          )}
          <p style={{ ...subText, textTransform: 'none', marginTop: '4px' }}>
            {side.abbreviation !== null ? `Abbreviation ${side.abbreviation}` : 'No abbreviation'}
            {side.states.length > 0 ? ` · ${side.states.join(', ')}` : ''}
            {side.regulatorOperatorId !== null
              ? ` · ${side.regulator ?? ''} ${side.regulatorOperatorId}`
              : ''}
          </p>
          <p style={{ fontSize: '13px', marginTop: '6px' }}>{side.reason}</p>
        </>
      )}
    </section>
  );
}

function PreviewBody({
  p,
  finalName,
  onFinalName,
  understood,
  onUnderstood,
}: {
  p: MergePreview;
  finalName: string;
  onFinalName: (s: string) => void;
  understood: boolean;
  onUnderstood: (b: boolean) => void;
}): React.JSX.Element {
  const c = p.counts;
  const moved = [
    [c.wellsMoved, 'wells'],
    [c.haulersMoved, 'hauler customer records'],
    [c.haulersFolded, 'hauler customer records folded into an existing one'],
    [c.portalCompaniesRepointed, 'operator portal companies'],
    [c.jobsRepointed, 'jobs'],
    [c.locationsRepointed, 'locations'],
    [c.siteContactsRepointed, 'site contacts'],
    [c.billableEventsRepointed, 'billable events'],
    [c.invoicesRepointed, 'invoices'],
    [c.summariesRepointed, 'summaries'],
    [c.disposalExclusionsRepointed, 'disposal exclusions'],
    [c.disposalFeesRepointed, 'disposal fees'],
  ] as const;
  const abbr = p.fields.abbreviation;
  const cell: React.CSSProperties = {
    padding: '6px 8px',
    borderBottom: '1px solid var(--color-border-secondary)',
    verticalAlign: 'top',
    fontSize: '13px',
  };

  return (
    <div style={{ marginTop: '14px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
      {p.warnings.length > 0 && (
        <div
          role="alert"
          style={{
            border: '1px solid var(--color-danger)',
            background: 'rgba(243, 139, 139,0.08)',
            borderRadius: '12px',
            padding: '10px 12px',
          }}
        >
          <ul
            style={{
              margin: 0,
              paddingLeft: '18px',
              color: 'var(--color-danger)',
              fontSize: '14px',
            }}
          >
            {p.warnings.map((w) => (
              <li key={w.code}>{w.text}</li>
            ))}
          </ul>
          <p style={{ fontSize: '13px', margin: '8px 0 0' }}>Usually the fix is Swap.</p>
          <label
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              marginTop: '8px',
              fontSize: '14px',
            }}
          >
            <input
              type="checkbox"
              checked={understood}
              onChange={(e): void => onUnderstood(e.target.checked)}
            />
            I understand
          </label>
        </div>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={{ ...cell, textAlign: 'left' }}>Field</th>
            <th style={{ ...cell, textAlign: 'left' }}>What happens</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={cell}>Abbreviation</td>
            <td style={cell}>
              kept: {abbr.kept ?? '—'} ·{' '}
              <span style={abbr.lost !== null ? { color: 'var(--color-danger)' } : undefined}>
                lost: {abbr.lost ?? '—'}
              </span>
              {abbr.lost !== null &&
                ' (abbreviations are unique; it stays on the alias row, unused)'}
            </td>
          </tr>
          <tr>
            <td style={cell}>Display name</td>
            <td style={cell}>
              Kept row: {p.fields.displayName.keep ?? '—'}; merged-away row:{' '}
              {p.fields.displayName.mergeAway ?? '—'}
              {p.fields.displayName.carried ? ' (carried, the kept row had none)' : ''}
            </td>
          </tr>
          <tr>
            <td style={cell}>State and regulator id</td>
            <td style={cell}>
              The kept row stays {p.fields.state.kept ?? '—'} and lists{' '}
              {p.fields.state.statesAfter.join(', ') || '—'}. The merged-away row keeps its state
              and id on its alias row.
            </td>
          </tr>
          <tr>
            <td style={cell}>Active</td>
            <td style={cell}>
              The merged-away row becomes inactive and stays as an alias, never deleted.
            </td>
          </tr>
          <tr>
            <td style={cell}>Moves to the kept row</td>
            <td style={cell}>
              {moved
                .filter(([n]) => n > 0)
                .map(([n, what]) => `${n.toLocaleString('en-US')} ${what}`)
                .join(' · ') || 'Nothing to move'}
            </td>
          </tr>
          {c.disposalFeesDropped > 0 && (
            <tr>
              <td style={cell}>Disposal fees in conflict</td>
              <td style={{ ...cell, color: 'var(--color-danger)' }}>
                Dropped: {c.disposalFeesDropped}
                <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
                  {p.droppedFees.map((f) => (
                    <li key={f.id}>
                      {f.feeCentsPerBbl !== undefined
                        ? `${formatNumber(f.feeCentsPerBbl / 100, { decimals: 2 })} $/bbl`
                        : 'A fee'}
                      {f.effectiveFrom !== undefined ? ` from ${f.effectiveFrom}` : ''}
                      {f.notes !== undefined && f.notes !== null && f.notes !== ''
                        ? ` (${f.notes})`
                        : ''}
                    </li>
                  ))}
                </ul>
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '14px' }}>
        Final display name (tickets and invoices print this for every merged row)
        <input
          aria-label="Final display name"
          value={finalName}
          onChange={(e): void => onFinalName(e.target.value)}
          placeholder={p.fields.displayName.proposed}
          maxLength={160}
          style={fieldStyle}
        />
      </label>

      <ul style={{ ...subText, textTransform: 'none', margin: 0, paddingLeft: '18px' }}>
        {p.caveats.map((t) => (
          <li key={t}>{t}</li>
        ))}
      </ul>
      <p style={{ fontSize: '13px', color: 'var(--color-danger)', margin: 0 }}>
        A merge cannot be undone.
      </p>
    </div>
  );
}
